/**
 * Boolean ops on closed LEATHER shapes (#764) — parts and stitch loops.
 *
 * The geometry is `shapeBooleanCore`, shared with the construction half. What
 * this module owns is which leather objects can be operands and what replacing
 * them means for the things attached to them.
 *
 * ## One kind at a time
 *
 * A selection has to be all parts or all stitch loops. Uniting a part with a
 * seam is not an operation with an answer — one is leather, the other is a line
 * of holes through it — so a mixed selection offers nothing rather than picking
 * a winner.
 *
 * ## What happens to what is attached
 *
 * A part carries holes, cut-outs, seams and decorations. The result takes the
 * FIRST operand's identity — its id, name and slot in the Parts list — and:
 *
 *  - **Unite** absorbs every operand's children. All of that leather is still
 *    there, so everything on it still belongs.
 *  - **Every other op** keeps only the first operand's children. The other
 *    parts are gone, and so is what was on them.
 *  - **Divide** hands each child to the region that CONTAINS it, so a hole
 *    stays with the piece of leather it is punched through. A child in none of
 *    the regions is dropped — it was in the part of the shape the op removed.
 *
 * Children of the surviving part that end up OUTSIDE the new outline are left
 * alone rather than pruned. They are still the maker's, the renderer already
 * clips stitch holes to the leather, and silently deleting a hole because an
 * outline moved would be a worse surprise than one drawn where it no longer
 * fits.
 *
 * ## Parts traced from construction are excluded
 *
 * A linked part's outline is re-derived from its construction source on every
 * commit, so a boolean on it would be overwritten by the next derive pass —
 * the same reason offset and mirror children are excluded on the construction
 * side.
 */
import type { EditorContext } from "../tools/EditorContext";
import {
  pieceStitchLines,
  type Piece,
  type Project,
  type StitchLine,
  type Vec2,
} from "./Project";
import { isStitchLoop } from "./stitching";
import { seamPathData } from "./seamGeometry";
import { newId } from "./ids";
import {
  canShapeBooleanWith,
  computeShapeBoolean,
  isClosedShapePath,
  MIN_OPERANDS,
  SHAPE_BOOLEAN_OPS,
  type ShapeBooleanOp,
} from "./shapeBooleanCore";

/** What a leather boolean is operating on. Resolved from the selection once so
 *  the toolbar and the action can't disagree about it. */
export type LeatherBooleanTarget =
  | { kind: "piece"; pieces: Piece[] }
  | { kind: "stitch"; pieceId: string; lines: StitchLine[] };

/** True when a part's outline is its own to change — i.e. not re-derived from
 *  a construction line on every commit. */
export function isBooleanablePiece(
  piece: Piece,
  scope: paper.PaperScope,
): boolean {
  if (piece.constructionOutline) return false;
  return isClosedShapePath(piece.shapePathData, scope);
}

/** True when a seam encloses a region — an open run of holes has no inside. */
export function isBooleanableStitch(
  line: StitchLine,
  scope: paper.PaperScope,
): boolean {
  if (!isStitchLoop(line)) return false;
  return isClosedShapePath(seamPathData(line), scope);
}

/**
 * Resolve the selection to a homogeneous operand set, or null.
 *
 * Parts come back in PROJECT order and seams in their part's own order, not
 * selection order — the same rule the construction half uses, and for the same
 * reason: a marquee produces a set, and Subtract needs a base that the user can
 * see and reorder.
 */
export function leatherBooleanTarget(
  project: Project,
  sel: {
    pieceIds?: string[];
    pieceId?: string | null;
    stitchIds?: string[];
    stitchId?: string | null;
  },
  scope: paper.PaperScope,
): LeatherBooleanTarget | null {
  const stitchIds = sel.stitchIds ?? (sel.stitchId ? [sel.stitchId] : []);
  if (stitchIds.length >= MIN_OPERANDS) {
    const wanted = new Set(stitchIds);
    // Seams are globally unique but a boolean only makes sense within ONE
    // part: the result is leather-relative geometry, and two parts have two
    // different pieces of leather.
    for (const piece of project.pieces) {
      const lines = pieceStitchLines(piece).filter(
        (l) => wanted.has(l.id) && isBooleanableStitch(l, scope),
      );
      if (lines.length >= MIN_OPERANDS && lines.length === wanted.size) {
        return { kind: "stitch", pieceId: piece.id, lines };
      }
    }
    return null;
  }
  const pieceIds = sel.pieceIds ?? (sel.pieceId ? [sel.pieceId] : []);
  if (pieceIds.length >= MIN_OPERANDS) {
    const wanted = new Set(pieceIds);
    const pieces = project.pieces.filter(
      (p) => wanted.has(p.id) && isBooleanablePiece(p, scope),
    );
    if (pieces.length >= MIN_OPERANDS) return { kind: "piece", pieces };
  }
  return null;
}

/** How many operands the resolved target holds. */
export function leatherOperandCount(target: LeatherBooleanTarget): number {
  return target.kind === "piece" ? target.pieces.length : target.lines.length;
}

/** True when the current selection can feed `op` on leather. */
export function canLeatherBooleanSelection(
  ctx: EditorContext,
  op: ShapeBooleanOp,
): boolean {
  const target = leatherBooleanTarget(
    ctx.history.state,
    ctx.scene.getSelection(),
    ctx.scene.getPaperScope(),
  );
  if (!target) return false;
  return canShapeBooleanWith(leatherOperandCount(target), op);
}

/** A representative point for deciding which divided region a child belongs
 *  to. Holes have a centre; everything else uses the middle of its bounds. */
function childAnchor(
  pathData: string,
  scope: paper.PaperScope,
): Vec2 | null {
  if (!pathData) return null;
  try {
    const cp = new scope.CompoundPath({ pathData, insert: false });
    const b = cp.bounds;
    const out = { x: b.center.x, y: b.center.y };
    cp.remove();
    return out;
  } catch {
    return null;
  }
}

/** Whether `point` falls inside `pathData`. Used to hand a part's children to
 *  the divided region they sit in. */
function regionContains(
  pathData: string,
  point: Vec2,
  scope: paper.PaperScope,
): boolean {
  try {
    const cp = new scope.CompoundPath({ pathData, insert: false });
    const inside = cp.contains(new scope.Point(point.x, point.y));
    cp.remove();
    return inside;
  } catch {
    return false;
  }
}

/** The children of `piece`, each with the point that decides its region. */
function pieceChildren(piece: Piece, scope: paper.PaperScope) {
  return {
    holes: piece.holes.map((h) => ({ item: h, at: h.position })),
    cutouts: piece.cutouts.map((c) => ({
      item: c,
      at: childAnchor(c.pathData, scope),
    })),
    stitchLines: pieceStitchLines(piece).map((l) => ({
      item: l,
      at: childAnchor(seamPathData(l), scope),
    })),
    decorations: piece.decorations.map((d) => ({
      item: d,
      at: childAnchor((d as unknown as { pathData?: string }).pathData ?? "", scope),
    })),
  };
}

/**
 * Selection-driven boolean op on leather.
 *
 * Returns false — committing nothing — when the selection isn't operable or the
 * op produces no area. Declining beats deleting: losing two parts to a mis-click
 * is worse than a button that turns out to have nothing to do.
 */
export function leatherBooleanSelection(
  ctx: EditorContext,
  op: ShapeBooleanOp,
): boolean {
  const scope = ctx.scene.getPaperScope();
  const target = leatherBooleanTarget(
    ctx.history.state,
    ctx.scene.getSelection(),
    scope,
  );
  if (!target) return false;
  if (!canShapeBooleanWith(leatherOperandCount(target), op)) return false;
  const spec = SHAPE_BOOLEAN_OPS.find((s) => s.op === op)!;

  if (target.kind === "stitch") {
    return commitStitchBoolean(ctx, target, op, spec.label, scope);
  }
  return commitPieceBoolean(ctx, target, op, spec.label, scope);
}

function commitStitchBoolean(
  ctx: EditorContext,
  target: Extract<LeatherBooleanTarget, { kind: "stitch" }>,
  op: ShapeBooleanOp,
  label: string,
  scope: paper.PaperScope,
): boolean {
  const built = computeShapeBoolean(
    op,
    target.lines.map((l) => seamPathData(l)),
    scope,
  );
  if (!built) return false;
  // A seam is ONE run of holes with no notion of a hole in the middle, so every
  // subpath of the result becomes its own seam — including the inner ring of a
  // shape with a hole, which is a row you would in fact stitch.
  const loops = built.flatMap((d) => pathDataToLoops(d, scope));
  if (loops.length === 0) return false;
  const base = target.lines[0]!;
  const consumed = new Set(target.lines.map((l) => l.id));
  const newIds = loops.map(() => newId("stitch"));

  ctx.commit(`${label} seams`, (proj) => ({
    ...proj,
    pieces: proj.pieces.map((pc) => {
      if (pc.id !== target.pieceId) return pc;
      const next: StitchLine[] = [];
      let inserted = false;
      for (const line of pieceStitchLines(pc)) {
        if (!consumed.has(line.id)) {
          next.push(line);
          continue;
        }
        if (inserted) continue;
        inserted = true;
        loops.forEach((loop, i) => {
          next.push({
            ...base,
            id: newIds[i]!,
            points: loop.points,
            ...(loop.handles ? { handles: loop.handles } : {}),
            closed: true,
            // The chisel settings are the maker's; only the path changed.
          });
        });
      }
      return { ...pc, stitchLines: next };
    }),
  }));
  ctx.setSelection({
    pieceId: target.pieceId,
    decorationId: null,
    stitchIds: newIds,
  });
  return true;
}

/** Split a boolean result into one closed loop per subpath, as seam points. */
function pathDataToLoops(
  pathData: string,
  scope: paper.PaperScope,
): Array<{ points: Vec2[]; handles?: StitchLine["handles"] }> {
  let cp: paper.CompoundPath;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return [];
  }
  const subs =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  const out: Array<{ points: Vec2[]; handles?: StitchLine["handles"] }> = [];
  for (const sub of subs) {
    const segs = sub.segments ?? [];
    if (segs.length < 3) continue;
    out.push({
      points: segs.map((s) => ({ x: s.point.x, y: s.point.y })),
      handles: segs.map((s) => ({
        in: { x: s.handleIn.x, y: s.handleIn.y },
        out: { x: s.handleOut.x, y: s.handleOut.y },
      })) as StitchLine["handles"],
    });
  }
  cp.remove();
  return out;
}

function commitPieceBoolean(
  ctx: EditorContext,
  target: Extract<LeatherBooleanTarget, { kind: "piece" }>,
  op: ShapeBooleanOp,
  label: string,
  scope: paper.PaperScope,
): boolean {
  const built = computeShapeBoolean(
    op,
    target.pieces.map((p) => p.shapePathData),
    scope,
  );
  if (!built) return false;
  const base = target.pieces[0]!;
  const consumed = new Set(target.pieces.map((p) => p.id));
  const newIds = built.map((_, i) => (i === 0 ? base.id : newId("piece")));

  // Unite keeps everything: all that leather still exists. Every other op
  // removes leather, so only the surviving part's own children carry over.
  const donors = op === "unite" ? target.pieces : [base];
  const merged = {
    holes: donors.flatMap((p) => p.holes),
    cutouts: donors.flatMap((p) => p.cutouts),
    stitchLines: donors.flatMap((p) => pieceStitchLines(p)),
    decorations: donors.flatMap((p) => p.decorations),
  };

  ctx.commit(`${label} parts`, (proj) => {
    const nextPieces: Piece[] = [];
    let inserted = false;
    for (const pc of proj.pieces) {
      if (!consumed.has(pc.id)) {
        nextPieces.push(pc);
        continue;
      }
      if (inserted) continue;
      inserted = true;
      built.forEach((pathData, i) => {
        // Divide hands each child to the region it sits in; the other ops put
        // everything on the single result.
        const keep =
          built.length === 1
            ? merged
            : {
                holes: pieceChildren(base, scope)
                  .holes.filter((h) => regionContains(pathData, h.at, scope))
                  .map((h) => h.item),
                cutouts: pieceChildren(base, scope)
                  .cutouts.filter(
                    (c) => c.at && regionContains(pathData, c.at, scope),
                  )
                  .map((c) => c.item),
                stitchLines: pieceChildren(base, scope)
                  .stitchLines.filter(
                    (l) => l.at && regionContains(pathData, l.at, scope),
                  )
                  .map((l) => l.item),
                decorations: pieceChildren(base, scope)
                  .decorations.filter(
                    (d) => d.at && regionContains(pathData, d.at, scope),
                  )
                  .map((d) => d.item),
              };
        nextPieces.push({
          ...base,
          id: newIds[i]!,
          name: i === 0 ? base.name : `${base.name} ${i + 1}`,
          shapePathData: pathData,
          holes: keep.holes,
          cutouts: keep.cutouts,
          stitchLines: keep.stitchLines,
          decorations: keep.decorations,
          // A boolean rebuilds the outline from scratch, so the metadata that
          // addresses the OLD anchors can't survive it.
          smoothAnchors: undefined,
          bezierMemory: undefined,
          cornerRounding: undefined,
          freehandSource: undefined,
        } as Piece);
      });
    }
    return { ...proj, pieces: nextPieces };
  });
  ctx.setSelection({ pieceId: newIds[0]!, decorationId: null, pieceIds: newIds });
  return true;
}
